import { LdButton } from "@emdgroup-liquid/liquid/dist/react";
import { PAM_TYPE } from "@src/constants";
import {
  StyledColumn,
  StyledLDMessage,
  StyledMessage,
  StyledRow,
} from "@src/styles";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormInput } from "../FormInput";
import { FormSelect } from "../FormSelect";
import { StyledButtonTab } from "./ButtonTab.style";
import { StopCodon } from "./StopCodon";

type ButtonTabPropsTypes = {
  onCodonChange: (input: any) => void;
  genome: string;
  clearButtonClick: boolean;
  onResetClick: () => void;
  isGenomeSpecific?: boolean;
};

export const ButtonTab = ({
  onCodonChange,
  genome,
  clearButtonClick,
  onResetClick,
  isGenomeSpecific = true,
}: ButtonTabPropsTypes) => {
  const [activeButton, setActiveButton] = useState<string>("base");
  const [pamType, setPamType] = useState<string>("");

  const { control } = useFormContext();

  const onButtonClick = (event: any) => {
    setActiveButton(event.target.id);
    sessionStorage.setItem("mode", event.target.id);
    onResetClick();
  };

  useEffect(() => {
    sessionStorage.setItem("mode", "base");
  }, []);

  // Force switch to base tab if genome specific is disabled while on codon tab
  useEffect(() => {
    if (!isGenomeSpecific && activeButton === "codon") {
      setActiveButton("base");
      sessionStorage.setItem("mode", "base");
    }
  }, [isGenomeSpecific, activeButton]);

  /** Helper: strips FASTA header and joins lines into a pure sequence string */
  const extractSequence = (value: string): string => {
    const lines = value.split("\n").filter(Boolean);
    if (lines.length > 0 && lines[0].includes(">")) {
      lines.shift();
    }
    return lines.join("");
  };

  /** Min length check: sequence must be >= 23 bp */
  const getSeqMnLength = (value: string): boolean => {
    return extractSequence(value).length >= 23;
  };

  /** Max length check: sequence must be <= 5000 bp */
  const getSeqMaxLength = (value: string): boolean => {
    const lines = value.split("\n").filter(Boolean);
    if (lines.length > 0 && lines[0].includes(">")) {
      lines.shift();
    }
    return lines.join("\n").length <= 5000;
  };

  /** ATGC-only check */
  const getAllowATGCOnly = (value: string): boolean => {
    const lines = value.split("\n").filter(Boolean);
    if (lines.length > 0 && lines[0].includes(">")) {
      lines.shift();
    }
    const strValue = lines.join("\n");
    for (let i = 0; i < strValue.length; i++) {
      const char = strValue.charAt(i).toUpperCase();
      if (!["A", "T", "G", "C", "\n"].includes(char)) {
        return false;
      }
    }
    return true;
  };

  const handlePamType = (value: string) => {
    setPamType(value);
  };

  return (
    <>
      <StyledRow>
        <StyledColumn width="100">
          <StyledButtonTab>
            <LdButton
              key="baseeditor-tab"
              type="button"
              id="base"
              onClick={onButtonClick}
              mode={activeButton === "base" ? "secondary" : "ghost"}
            >
              DNA Base Editor Design
            </LdButton>
            {isGenomeSpecific && (
              <LdButton
                key="codon-tab"
                type="button"
                onClick={onButtonClick}
                id="codon"
                mode={activeButton === "codon" ? "secondary" : "ghost"}
              >
                Stop Codon Design
              </LdButton>
            )}
          </StyledButtonTab>
        </StyledColumn>
      </StyledRow>

      <StyledRow gap={550}>
        {activeButton === "base" && (
          <StyledColumn width={25}>
            <FormSelect
              name="pam_type"
              label="PAM Type"
              options={PAM_TYPE}
              placeholder="Select PAM Type"
              rules={{
                required: {
                  value: true,
                  message: "This is a required field.",
                },
              }}
              onChange={handlePamType}
            />
            {(pamType === "NAG" || pamType === "NGA") && (
              <StyledMessage width={200} color={"#0f69af"}>
                <StyledLDMessage mode="info">
                  Non-NGG PAMs would likely have lower editing efficiency
                </StyledLDMessage>
              </StyledMessage>
            )}
          </StyledColumn>
        )}
        <StyledColumn width={40}>
          {/* gRNA Length selector commented out */}
        </StyledColumn>
      </StyledRow>

      <StyledRow gap={10}>
        <StyledColumn width={100}>
          {activeButton === "base" && (
            <FormInput
              placeholder={`FASTA format
                
>seq
GACCATCCAGGAGGTGGCTGGTTATGTCCTCATTGCCCTCAACACAGTGGAGCGAATTCCTTTGGAAAAC

OR plain text sequence without header
GACCATCCAGGAGGTGGCTGGTTATGTCCTCATTGCCCTCAACACAGTGGAGCGAATTCCTTTGGAAAAC`
              }
              name="user_input_sequence"
              label="Enter Target sequence"
              multiline
              rows={7}
              rules={{
                required: {
                  value: true,
                  message: "This is a required field.",
                },
                validate: {
                  seqMaxLength: (value) =>
                    getSeqMaxLength(value) ||
                    "Input sequence length should be less than 5000 bp",
                  seqMinLength: (value) =>
                    getSeqMnLength(value) ||
                    "Input sequence length should be at least 23 bp",
                  allowSingleSeq: (value) =>
                    value.split(">").length <= 2 ||
                    "Mutiple sequences NOT allowed",
                  allowOnlyATGC: (value) =>
                    getAllowATGCOnly(value) ||
                    "Only ATGC allowed in sequence/Invalid sequence",
                },
              }}
            />
          )}
          {activeButton === "codon" && isGenomeSpecific && (
            <StopCodon
              onCodonChange={onCodonChange}
              genome={genome}
              clearButtonClick={clearButtonClick}
            />
          )}
        </StyledColumn>
      </StyledRow>
    </>
  );
};